import React, { useEffect, useRef } from "react";
import { wormColors } from "core/src/core/constants.js";
import requestFrame from "../../../shared/request-frame.js";
import { VICTORY_ANIMS, createFireworksState, drawVictoryAnim, isVictoryAnim, type VictoryAnimType } from "../../../game-canvas/victory-anim";

// The winner's celebration, playing around their hero on the results screen:
// the same canvas animation their worm performs on the victory lap. It draws
// on a canvas covering the WHOLE viewport (behind the page's content), aimed at
// the avatar's on-screen position each frame, so rockets, beams and spray can
// travel as far as they like and nothing is ever cut off at a box's edge. Only
// for a winner who OWNS a celebration (a shop cosmetic): with none, nothing
// plays here, the same as on the board. Decoration only: hidden from screen
// readers and pointer events.

// The celebration a winner plays on this screen, or null for none. The
// ?fxcycle=1 design aid plays every celebration in turn regardless.
export function celebrationFor(player: { victoryAnim?: string | null }): VictoryAnimType | null {
  if (isVictoryAnim(player.victoryAnim)) return player.victoryAnim;
  return celebrationCycling() ? VICTORY_ANIMS[0] : null;
}
// Celebrations that bring their own crown, so the hero doesn't wear a second one.
export function celebrationHasCrown(type: VictoryAnimType): boolean {
  return type === "coronation" || type === "champagne";
}
// Design aid: `?fxcycle=1` on the results screen plays EVERY celebration in
// turn, CYCLE_SECONDS each, so all of them can be checked around the hero
// without winning a match per cosmetic. Off unless asked for in the URL.
const CYCLE_SECONDS = 3;
export function celebrationCycling(): boolean {
  try { return new URLSearchParams(window.location.search).get("fxcycle") === "1"; } catch { return false; }
}

interface Props {
  player: { colorId: string; flag?: string | null; victoryAnim?: string | null };
  // The element the celebration plays around (the hero's avatar).
  anchorRef: React.RefObject<HTMLElement | null>;
  className?: string;
}

export default function WinnerCelebration({ player, anchorRef, className }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const cycling = celebrationCycling();
    const first = celebrationFor(player);
    if (!first) return; // no celebration owned: nothing plays
    const color = wormColors[player.colorId] || "#ffcf4d";
    // The celebration plays around the AVATAR DISC at the size it has around a
    // normal worm head (scale), so its props clear the ring instead of landing
    // inside it. The crown ones (coronation, champagne) hover 16 size-units
    // above the "head" edge, which reads too far off a big disc, so for them the
    // radius handed in is pulled in by half that hover: the crown sits half the
    // authored gap above the ring. The cowboy hat sits right on the head (4
    // units), so it gets the full ring radius and lands just above the ring.
    const SCALE = 2.6;
    const opts = { fwState: createFireworksState(), fwHeading: -Math.PI / 2, scale: SCALE };
    const pullIn = (t: VictoryAnimType) => (t === "coronation" || t === "champagne" ? 8 * SCALE : 0);
    const clearance = (r: DOMRect, t: VictoryAnimType) => r.width / 2 + 4 - pullIn(t);
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let id = 0;
    let running = true;
    let w = 0, h = 0, dpr = 1;
    let t0 = performance.now();
    let type: VictoryAnimType = first;
    let cycleIndex = 0;
    const frame = () => {
      if (!running) return;
      if (cycling && performance.now() - t0 > CYCLE_SECONDS * 1000) {
        // next celebration, from its own t = 0
        cycleIndex = (cycleIndex + 1) % VICTORY_ANIMS.length;
        type = VICTORY_ANIMS[cycleIndex];
        t0 = performance.now();
        opts.fwState = createFireworksState();
      }
      // Track the viewport (a resize, a rotated phone) and the anchor (scroll).
      const nd = Math.min(2, window.devicePixelRatio || 1);
      const nw = window.innerWidth, nh = window.innerHeight;
      if (nw !== w || nh !== h || nd !== dpr) {
        w = nw; h = nh; dpr = nd;
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const a = anchorRef.current;
      if (a) {
        const r = a.getBoundingClientRect();
        drawVictoryAnim(ctx, r.left + r.width / 2, r.top + r.height / 2, clearance(r, type), color, player.flag, (performance.now() - t0) / 1000, type, opts);
      }
      if (!reduced) id = requestFrame(frame);
    };
    id = requestFrame(frame);
    return () => {
      running = false;
      window.cancelAnimationFrame(id);
    };
  }, [player.colorId, player.flag, player.victoryAnim, anchorRef]);
  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
